import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminAccountTypeChangeDTO,
  AdminQuotaAddExtraDTO,
  AdminQuotaConfigDTO,
  AdminQuotaResetDTO,
} from '@shared/schemas/quota';
import {
  addAdminQuotaExtra,
  changeAdminUserAccountType,
  configureAdminUserQuota,
  getAdminUserQuotaDetail,
  listAdminQuotaEvents,
  listAdminQuotaPeriods,
  recomputeAdminUserQuota,
  resetAdminUserQuota,
} from './quota-api';
import { quotaQueryKeys } from './quota-query-keys';

const HISTORY_PAGE_SIZE = 10;

export function useAdminUserQuotaDetail(userId: string | undefined) {
  return useQuery({
    enabled: Boolean(userId),
    queryFn: () => getAdminUserQuotaDetail(userId!),
    queryKey: quotaQueryKeys.detail(userId ?? 'unknown'),
  });
}

export function useAdminQuotaEvents(userId: string | undefined, page: number) {
  return useQuery({
    enabled: Boolean(userId),
    queryFn: () => listAdminQuotaEvents(userId!, { page, pageSize: HISTORY_PAGE_SIZE }),
    queryKey: quotaQueryKeys.events(userId ?? 'unknown', page, HISTORY_PAGE_SIZE),
  });
}

export function useAdminQuotaPeriods(userId: string | undefined, page: number) {
  return useQuery({
    enabled: Boolean(userId),
    queryFn: () => listAdminQuotaPeriods(userId!, { page, pageSize: HISTORY_PAGE_SIZE }),
    queryKey: quotaQueryKeys.periods(userId ?? 'unknown', page, HISTORY_PAGE_SIZE),
  });
}

function useInvalidateAdminQuota(userId: string) {
  const queryClient = useQueryClient();

  return {
    invalidateDetail: () => queryClient.invalidateQueries({ queryKey: quotaQueryKeys.detail(userId) }),
    invalidateEvents: () => queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === quotaQueryKeys.all[0]
        && query.queryKey[1] === 'events'
        && query.queryKey[2] === userId,
    }),
    invalidatePeriods: () => queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === quotaQueryKeys.all[0]
        && query.queryKey[1] === 'periods'
        && query.queryKey[2] === userId,
    }),
  };
}

export function useConfigureQuotaMutation(userId: string) {
  const invalidation = useInvalidateAdminQuota(userId);

  return useMutation({
    mutationFn: (body: AdminQuotaConfigDTO) => configureAdminUserQuota(userId, body),
    onSuccess: async () => {
      await Promise.all([
        invalidation.invalidateDetail(),
        invalidation.invalidateEvents(),
      ]);
    },
  });
}

export function useAddExtraQuotaMutation(userId: string) {
  const invalidation = useInvalidateAdminQuota(userId);

  return useMutation({
    mutationFn: (body: AdminQuotaAddExtraDTO) => addAdminQuotaExtra(userId, body),
    onSuccess: async () => {
      await Promise.all([
        invalidation.invalidateDetail(),
        invalidation.invalidateEvents(),
      ]);
    },
  });
}

export function useResetQuotaMutation(userId: string) {
  const invalidation = useInvalidateAdminQuota(userId);

  return useMutation({
    mutationFn: (body: AdminQuotaResetDTO) => resetAdminUserQuota(userId, body),
    onSuccess: async () => {
      await Promise.all([
        invalidation.invalidateDetail(),
        invalidation.invalidateEvents(),
        invalidation.invalidatePeriods(),
      ]);
    },
  });
}

export function useRecomputeQuotaMutation(userId: string) {
  const invalidation = useInvalidateAdminQuota(userId);

  return useMutation({
    mutationFn: () => recomputeAdminUserQuota(userId),
    onSuccess: async () => {
      await Promise.all([
        invalidation.invalidateDetail(),
        invalidation.invalidateEvents(),
      ]);
    },
  });
}

export function useChangeAccountTypeMutation(userId: string) {
  const invalidation = useInvalidateAdminQuota(userId);

  return useMutation({
    mutationFn: (body: AdminAccountTypeChangeDTO) => changeAdminUserAccountType(userId, body),
    onSuccess: async () => {
      await Promise.all([
        invalidation.invalidateDetail(),
        invalidation.invalidateEvents(),
      ]);
    },
  });
}

export { HISTORY_PAGE_SIZE };
